
import { Component, Vue, Prop } from 'vue-property-decorator'
import { Domain, Workspace, DomainSecurityLevel, MapProvider, FullTextSearchType, TrackingMode, DomainType } from '@/types/Catalogs'
import DateSelector from '@/components/generic/DateSelector.vue'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import { MessageError } from '@/components/generic/MessageHandling.vue'
import catalogsService from '@/services/CatalogsService'
import messageService from '@/services/support/MessageService'
import { RoleNames } from '@/types/Auth'
import userInfoStore from '@/store/userInfo'
import DateFormat from '@/services/DateFormat'

@Component({
  components: {
    DateSelector,
    LoadingMask,
    MessageError
  }
})
export default class AdminDomainForm extends Vue {
  @Prop({ required: true, default: 0 })
  id: number

  @Prop({ required: false, default: 0 })
  idClient: number

  @Prop({ required: true, default: '' })
  title: string

  @Prop({ required: false, default: false })
  readOnly: boolean

  @Prop({ required: false, default: false })
  editButton: boolean

  @Prop({ required: false, default: () => [] })
  workspaces: []

  @Prop({ required: false, default: false })
  showOpenCloseButton: boolean

  MAX_WORKSPACES_FOR_INDIVIDUAL_DOMAIN = 2
  loading = false
  showPasswordRequirements: boolean = true
  domainLevels = DomainSecurityLevel

  form = Domain.getIntanceForEdit()
  formWorkspace = Workspace.getIntanceForEdit()

  securityLevelOptions = [
    { value: '', text: '-- Seleccione --' },
    { value: DomainSecurityLevel.LOW, text: 'Bajo' },
    { value: DomainSecurityLevel.MEDIUM, text: 'Medio' },
    { value: DomainSecurityLevel.HIGH, text: 'Alto' }
  ]
  yesNoOptions = [ { value: false, text: 'No' }, { value: true, text: 'Sí' } ]
  mapProviderOptions = [
    { value: '', text: '-- Seleccione --' },
    { value: MapProvider.MAPBOX, text: 'MAPBOX' },
    { value: MapProvider.GOOGLE, text: 'GOOGLE' }
  ]
  fullTextSearchTypeOptions = [
    { value: '', text: '-- Seleccione --' },
    { value: FullTextSearchType.ELASTIC, text: 'Elastic Search' }
  ]
  trackerModeOptions = [
    { value: '', text: 'Desactivado' },
    { value: TrackingMode.PASIVO, text: 'Pasivo' },
    { value: TrackingMode.INTENSIVO, text: 'Intensivo' }
  ]
  productLineOptions = [ { value: 'Smartforms', text: 'Smartforms' } ]
  domainTypesOptions = [
    { value: '', text: '-- Seleccione --' },
    { value: DomainType.ECOSYSTEM, text: 'Ecosistema' },
    { value: DomainType.INDIVIDUAL, text: 'Cliente individual' }
  ]
  regexPhone = /^[+]?[0-9]{10,15}$/
  regexBusinessSegment = /^[0-9]{3}$/
  regexBillingCode = /^[a-zA-Z0-9]{10}$/
  regexDomainName = /^[0-9a-zA-Z_-]{3,50}$/

  creationDate = ''
  updateDate = ''

  domainBackup = ''

  mounted () {
    this.init()
  }

  init () {
    if (this.id) {
      this.loading = true
      catalogsService.getDomainById(this.id).then(data => {
        this.creationDate = data.creationDate ? new DateFormat(data.creationDate).toDateTimeString() : '- - / - - / - - - -'
        this.updateDate = data.updateDate ? new DateFormat(data.updateDate).toDateTimeString() : '- - / - - / - - - -'
        data.trackingMode = data.trackingMode || ''
        data.mapProvider = data.mapProvider || ''
        data.broadcast = data.broadcast || ''

        Object.assign(this.form, data)

        this.domainBackup = JSON.stringify(this.form)
        this.$validator.reset()
      }).finally(() => {
        this.loading = false
      })
    }
  }

  get editOperationsDataPermission () {
    return userInfoStore.userInfo.role.name === RoleNames.OPERATIONS_ADMIN
  }

  get editAdminDataPermission () {
    return userInfoStore.userInfo.role.name === RoleNames.FINANCES_ADMIN || userInfoStore.userInfo.role.name === RoleNames.FINANCES_CONTROL
  }

  /**
   * Check if the current ecosystem domain can change to individual
   */
  get couldBeIndividual (): boolean {
    return this.workspaces.length <= this.MAX_WORKSPACES_FOR_INDIVIDUAL_DOMAIN
  }

  validateState (ref: any) {
    const thiz = this as any
    if (
      thiz.veeFields[ref] &&
      (thiz.veeFields[ref].dirty || thiz.veeFields[ref].validated)
    ) {
      return !thiz.veeErrors.has(ref)
    }
    return null
  }

  onSubmit () {
    this.$validator.validateAll().then(result => {
      if (result) {
        if (JSON.stringify(this.form) === this.domainBackup) {
          // If there are no changes then return to main grid
          this.onCancel()
          return
        }

        this.loading = true
        this.form.updateDate = new Date()
        if (!this.form.id) this.form.creationDate = new Date()
        catalogsService.saveDomain(this.form).then((response: Domain) => {
          this.form.id = response.id

          if (this.idClient) {
            this.formWorkspace.client.id = this.idClient
            this.formWorkspace.domain.id = this.form.id
            this.formWorkspace.owner = true
            catalogsService.saveWorkspace(this.formWorkspace).then((response: Workspace) => {
              this.$emit('saved', this.form)
            }).catch((error) => {
              messageService.error('Error al guardar la relación Cliente - Dominio', `${error.response!.data!.description} - ${error.response!.data!.message}`)
            }).finally(() => {
              this.loading = false
            })
          } else {
            this.$emit('saved', this.form)
          }
        }).catch((error) => {
          messageService.error('Error al guardar el dominio.', `${error.response!.data!.description} - ${error.response!.data!.message}`)
        }).finally(() => {
          this.loading = false
        })
      } else {
        messageService.error('Errores en el formulario', 'Revise los campos marcados con error', 4500)
      }
    })
  }

  onEdit () {
    this.$emit('edit')
  }

  onCancel () {
    this.$emit('close')
  }

  handleOpenCloseDomain () {
    this.$emit('open-close-domain', this.form)
  }
}
